import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, 
  // Alert, AlertIcon 
} from '@chakra-ui/react';
import { useBookAppointmentStore } from '../store';
import { useAppointmentBooking } from '../hooks/useAppointmentBooking';
import { AppointmentDetails } from './ConformationStep/AppointmentDetails';
import { ServicePaymentConfirmation } from './ConformationStep/ServicePaymentConfirmation';
import BookingSuccess from './BookingSuccess';
import PaymentStep from './PaymentStep';
import { patientStore } from '../store/PatientStore';
import { appointmentTypesStore } from '../store/AppointmentTypesStore';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { bookingFlowStore } from '../store/BookingFlowStore';
import { toISOString } from '../lib/util';
import axios from "axios";
import { useAlert } from './AlertProvider';
import { authStore } from '../store/AuthStore';
import { cancelBookedAppointment } from '../services/PatientService';
// import { clinicStore } from '../store/ClinicStore';

interface ServiceConfirmationStepProps {
  onClose: () => void;
  selectedTime: {
    formattedTime: string;
    date: string;
    time: string;
    duration: string;
  } | null;
}

const ServiceConfirmationStep: React.FC<ServiceConfirmationStepProps> = observer(({ onClose, selectedTime }) => {
  const [step, setStep] = useState<number>(1);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<any>(null);

  const { isLoading, setIsLoading } = useAppointmentBooking();
  // eslint-disable-next-line 
  const [patient, doctor, clinic, patientId, appointmentId, setData, resetData] = useBookAppointmentStore((state) => [
    state.patient,
    state.doctor,
    state.clinic,
    state.patientId,
    state.appointmentId,
    state.setData,
    state.resetData,
  ]);

  // const { showAlert: showPopUpAlert } = useAlert();

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  const borderColor = isServiceBooking
    ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg
    : "#B92F2F";
  const buttonColor = borderColor;

  const { showAlert: showPopUpAlert } = useAlert();

  const clinicId = `${doctor.clinic_group_crm}-${doctor.user_fk}-${doctor.location_id}`;
  const appointmentIdFromStore = servicebookingFlowStore?.appointmentData?.appointmentType?.appointment_type_id?.toString();

  const handlePaymentYes = () => setStep(3);
  const handlePaymentNo = () => {
      cancelBookedAppointment(clinicId, appointmentId)
        .then(() => {
          showPopUpAlert({
            severity: "success",
            title: "Success",
            message: "Appointment cancelled successfully.",
          });
        })
        .catch((error) => {
          showPopUpAlert({
            severity: "error",
            title: "Error",
            message: error?.response?.data?.message || error?.message || "Something went wrong",
          });
        });
      onClose();
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
    setIsLoading(false);
    window.location.reload();
  };

  const handleConfirmAppointment = async () => {

    setIsLoading(true);

    try {
      const payload = {
        clinic_id: clinicId,
        doctor_id: doctor.doctor_id,
        appointment_type_id: appointmentIdFromStore,
        date: selectedTime?.date,
        time: selectedTime?.time,
      };

      // await post("clinics/createappointment", payload);
      const accessToken = await authStore.getAccessToken();
      const response = await axios.post(
        "https://ai.staging.app.familydoctor.com.au/clinics/createappointment",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("response", response);

      // Extract patient_id and appointment_id from response
      const appointmentId = response.data?.requestAppointmentResult?.appointment_id;
      const patientId = response.data?.patient_id;

      // Save to store
      setData({
        patientId, // doesn't do anything
        appointmentId,
      });
      patientStore.setPatientId(patientId);

      console.log("patientid", patientId);
      console.log("appointmentId", appointmentId);

      const duration = selectedTime?.duration?.toString() || "0";
      // Track event
      (window as any).gtag('event', isServiceBooking ? 'service_booking_complete' : 'booking_complete', {
        clinic_group_crm: doctor.clinic_group_crm,
        user_fk: doctor.user_fk,
        location_id: doctor.location_id,
      });

      // Update appointment details
      const appointmentDetails = {
        title: `Appointment with ${doctor?.display_name || 'Doctor'}`,
        description: `Appointment for ${patient?.first_name || 'N/A'} ${patient?.last_name || 'N/A'}`,
        startTime: toISOString(new Date(`${selectedTime?.date}T${selectedTime?.time}`)),
        endTime: toISOString(new Date(new Date(`${selectedTime?.date}T${selectedTime?.time}`).getTime() + parseInt(duration) * 60000)),
        location: clinic?.centre_name || 'N/A',
        appointmentId,
      };

      setAppointmentDetails(appointmentDetails);
      bookingFlowStore.setAppointmentDetails(appointmentDetails);
      servicebookingFlowStore.setAppointmentDetails(appointmentDetails);

      console.log("appointmentdetails", appointmentDetails);

      if (
        appointmentTypesStore.selectedAptType?.pre_payment_req === '1' ||
        appointmentTypesStore.selectedAptType?.post_payment_req === '1'
      ) {
        setStep(2);
        console.log("step2");
        return;
      } else {
        setStep(4);
        console.log("step3");
        return;
      }
    } catch (error) {
      console.error("Failed to submit appointment data:", error);
      showPopUpAlert({
        severity: "error",
        title: "Whoops",
        message: error?.response?.data?.message || error?.message || "Something went wrong"
      });
      setIsLoading(false);
      return;
    }finally {
      setIsLoading(false);
    }
  };

  // Main render logic
  return (
    <Box width={{ base: "100%" }} p={1} display="flex" flexDirection="column" alignItems="center">
      {step === 1 && (
        <>
          {/* {(clinicStore?.currentClinic?.confirm_app_info || clinicStore?.currentClinic?.confirm_app_holiday_info) && (
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <AlertIcon boxSize="40px" mr={0} mb={2} />
              <p dangerouslySetInnerHTML={{ __html: clinicStore?.currentClinic?.confirm_app_info || "" }} />
            </Alert>
          )} */}

          <AppointmentDetails
            patient={patientStore.currentPatient}
            selectedTime={selectedTime}
            doctor={doctor}
            clinic={clinic}
          />

          <Button
            bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
            color="#fff"
            width={{ base: "100%", md: "50%" }}
            borderRadius="30px"
            mt="20px"
            onClick={handleConfirmAppointment}
            isLoading={isLoading}
          >
            Confirm Appointment
          </Button>
          <Button
            variant="outline"
            borderColor={buttonColor}
            color={buttonColor}
            border="2px"
            width={{ base: "100%", md: "50%" }}
            borderRadius="30px"
            mt={4}
            onClick={async () => { servicebookingFlowStore.goToTime(); }}
          >
            Back
          </Button>
        </>
      )}

      {step === 2 && (
        <ServicePaymentConfirmation
          appointmentType={appointmentTypesStore.selectedAptType}
          borderColor={borderColor}
          isServiceBooking={isServiceBooking}
          handlePaymentNo={handlePaymentNo}
          handlePaymentYes={handlePaymentYes}
          isPatientValid={!!patientStore.currentPatient?.id}
          servicebookingFlowStore={servicebookingFlowStore}
        />
      )}

      {step === 3 && (
        <PaymentStep
          clinic={clinic}
          patient={patientStore.currentPatient}
          patientId={patientId}
          doctor={doctor}
          locationId={doctor?.location_id}
          pmethod={appointmentTypesStore.selectedAptType?.pre_payment_req === '1' ? '2' : '3'}
          aptypeid={appointmentTypesStore.selectedAptType?.appointment_type_id}
          bid={appointmentId}
          fees={''}
          isSuccessModalOpen={isSuccessModalOpen}
          onSuccessModalClose={closeSuccessModal}
          handleSuccess={() => setStep(4)}
          handleError={() => cancelBookedAppointment(clinicId, appointmentIdFromStore)}
          handlePrevious={handlePaymentNo}
        />
      )}

      {step === 4 && (
        <BookingSuccess
          onClose={closeSuccessModal}
          appointmentDetails={appointmentDetails}
        />
      )}
    </Box>
  );
});

export default ServiceConfirmationStep;
